import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { money, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Search, Package, Star } from "lucide-react";

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/store/products", { params: { search: search || undefined } });
    setProducts(data);
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Products</h1>
          <p className="text-muted-foreground mt-1">Manage your catalog, pricing, and inventory.</p>
        </div>
        <Link to="/dashboard/products/new">
          <Button className="rounded-lg" data-testid="add-product-btn"><Plus className="h-4 w-4 mr-1.5" /> Add product</Button>
        </Link>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products, SKU, tags..."
          className="pl-9 h-11" data-testid="product-search-input" />
      </div>

      <Card className="rounded-xl border-border overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading...</div>
        ) : products.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="font-medium">No products yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add your first product to get started.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/dashboard/products/${p.id}`)}
                  data-testid={`product-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-lg bg-secondary overflow-hidden flex items-center justify-center shrink-0">
                        {p.images?.[0] ? <img src={fileUrl(p.images[0])} alt="" className="h-full w-full object-cover" />
                          : <Package className="h-5 w-5 text-muted-foreground" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate flex items-center gap-1.5">
                          {p.name}
                          {p.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                        </p>
                        {p.customer_prices?.length > 0 && (
                          <p className="text-xs text-primary">{p.customer_prices.length} custom price{p.customer_prices.length > 1 ? "s" : ""}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{p.sku || "—"}</TableCell>
                  <TableCell className="text-right">
                    <span className={p.quantity === 0 ? "text-destructive font-medium" : ""}>{p.quantity}</span>
                  </TableCell>
                  <TableCell className="text-right font-medium">{money(p.base_price)}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={`border-0 rounded-full ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {p.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
